import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Modal } from '@/components/dialogs/Modal';
import { TextField } from '@/components/forms/TextField';
import { Select2Field } from '@/components/forms/Select2Field';
import type { SelectOption } from '@/components/forms/SelectField';
import { MAX_EMAIL_LENGTH } from '@/api/types/emailRecipients';

export interface RecipientFormValues {
  group: string; // kept as string for the <select>; coerced by the caller
  email: string;
}

const schema = z.object({
  group: z.string(),
  email: z
    .string()
    .trim()
    .min(1, 'Please enter an email address.')
    .email('Please enter a valid email address.')
    .max(MAX_EMAIL_LENGTH, `Email must be at most ${MAX_EMAIL_LENGTH} characters.`),
});

interface RecipientFormModalProps {
  show: boolean;
  mode: 'create' | 'edit';
  groupOptions: SelectOption[];
  initial?: { email: string; groupLabel: string };
  busy?: boolean;
  onSubmit: (values: RecipientFormValues) => void;
  onClose: () => void;
}

export function RecipientFormModal({
  show,
  mode,
  groupOptions,
  initial,
  busy = false,
  onSubmit,
  onClose,
}: RecipientFormModalProps) {
  const createSchema =
    mode === 'create'
      ? schema.refine((v) => v.group !== '', { path: ['group'], message: 'Please select a group.' })
      : schema;

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<RecipientFormValues>({
    resolver: zodResolver(createSchema),
    defaultValues: { group: '', email: initial?.email ?? '' },
  });

  return (
    <Modal
      show={show}
      title={mode === 'create' ? 'Add Recipient' : 'Edit Recipient'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-light" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" form="recipient-form" className="btn btn-primary" disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <form id="recipient-form" onSubmit={handleSubmit(onSubmit)} noValidate>
        {mode === 'create' ? (
          <Select2Field
            control={control}
            name="group"
            label="Group"
            placeholder="Select a group…"
            options={groupOptions}
          />
        ) : (
          // Group cannot change after creation (backend enforces delete + re-add).
          <div className="mb-3">
            <label className="form-label">Group</label>
            <input className="form-control" value={initial?.groupLabel ?? ''} disabled readOnly />
          </div>
        )}

        <TextField
          label="Email"
          type="email"
          autoComplete="off"
          error={errors.email?.message}
          {...register('email')}
        />
      </form>
    </Modal>
  );
}
